import Breadcrumb from "components/Common/Breadcrumb"
import TableContainer from "components/Common/TableContainer"
import InvoiceWhatsAppCell from "components/InvoiceWhatsAppCell"
import { SimpleStringValue } from "helpers/common_helper_functions"
import React, { useEffect, useMemo, useState } from "react"
import { Col, Row, Card, CardBody, Container, Badge, Button } from "reactstrap"
import dayjs from "dayjs"
import { DateRangePicker } from "rsuite"
import { getPredefinedDateRanges } from "helpers/dateRanges"
import "rsuite/dist/rsuite.min.css"
import { getUserInfo } from "helpers/authHelper"
import { getReport, regenerateSubscriptionInvoice } from "helpers/backendHelpers/manageSubscription"
import { SaveToast } from "components/Common/SaveToast"
import { exportStyledXlsx } from "helpers/exportExcel"
import { CSVLink } from "react-csv"

const Index = () => {
  const [report, setReport] = useState([])
  const [reportList, setReportList] = useState([])
  const [date, setDate] = useState({
    startDate: dayjs().startOf("month").format("YYYY-MM-DD") + "T00:00:00.000Z",
    endDate: dayjs().endOf("month").format("YYYY-MM-DD") + "T00:00:00.000Z",
  })
  const [regeneratingId, setRegeneratingId] = useState(null)

  const handleRegenerateInvoice = async (paymentId) => {
    if (!paymentId || regeneratingId) return
    try {
      setRegeneratingId(paymentId)
      const res = await regenerateSubscriptionInvoice(paymentId)
      if (res?.statusCode === 200 || res?.status === 200 || res?.success !== false) {
        SaveToast({ message: "Invoice generated successfully", type: "success" })
        await fetchReport(date.startDate, date.endDate)
      } else {
        SaveToast({ message: res?.message || "Failed to generate invoice", type: "error" })
      }
    } catch (error) {
      SaveToast({ message: error?.response?.data?.message || error?.message || "Failed to generate invoice", type: "error" })
    } finally {
      setRegeneratingId(null)
    }
  }

  useEffect(() => {
    fetchReport(date.startDate, date.endDate)
  }, [])

  const reportTotals = useMemo(() => {
    return report.reduce(
      (totals, row) => {
        totals.planAmount += Number(row["Plan Amount"] || 0)
        totals.discountAmount += Number(row["Discount Amount"] || 0)
        totals.taxableAmount += Number(row["Taxable Amount"] || 0)
        totals.taxAmount += Number(row["Tax Amount"] || 0)
        totals.totalAmount += Number(row["Total Amount"] || 0)
        return totals
      },
      {
        planAmount: 0,
        discountAmount: 0,
        taxableAmount: 0,
        taxAmount: 0,
        totalAmount: 0,
      }
    )
  }, [report])

  const csvReportData = useMemo(() => {
    return [
      ...report,
      {
        "Invoice No": "Total",
        "Invoice Date": "",
        "Customer Name": "",
        "Firm Name": "",
        "GST NO.": "",
        "Plan Name": "",
        "Plan Type": "",
        "Plan Amount": reportTotals.planAmount,
        "Discount Amount": reportTotals.discountAmount,
        "Taxable Amount": reportTotals.taxableAmount,
        "Tax Amount": reportTotals.taxAmount,
        "Total Amount": reportTotals.totalAmount,
        "Status": "",
      },
    ]
  }, [report, reportTotals])

  const exportXlsx = () => {
    exportStyledXlsx({
      rows: csvReportData,
      fileName: `Bill-Saathi-${dayjs(date.startDate).format("DD-MM-YYYY")}-${dayjs(date.endDate).format("DD-MM-YYYY")}`,
      cancelledRowPredicate: row => row.Status === "Cancelled",
    })
  }


  const fetchReport = async (startDate, endDate) => {
    try {
      let reports = await getReport(startDate, endDate)
      reports = reports?.payload?.data || []
      setReportList(reports)
      reports = reports.map(val => {
        const planAmount = Number(val?.planAmount || 0)
        const discountAmount = Number(val?.discountAmount || 0)
        return {
          "Invoice No": val?.invoiceNo,
          "Invoice Date": new Date(val?.createdAt).toLocaleDateString(),
          "Customer Name": val?.userDetails?.name,
          "Firm Name": val?.userDetails?.firmName,
          "GST NO.": val?.userDetails?.GSTNo,
          "Plan Name": val?.name,
          "Plan Type": val?.isPlanRenew ? "Renew" : "New",
          "Plan Amount": planAmount,
          "Discount Amount": discountAmount,
          "Taxable Amount": discountAmount ? parseInt(planAmount - discountAmount) : planAmount,
          "Tax Amount": Number(val?.taxAmount || 0),
          "Total Amount": Number(val?.finalAmount || 0),
          "Status": val?.invoiceStatus === "cancelled" || val?.isInvoiceCancelled ? "Cancelled" : "Paid",
          "Invoice Send": val?.invoiceWhatsAppStatus === "sent"
            ? `${Math.max(1, Number(val?.invoiceWhatsAppSendCount || 0))} Sent`
            : val?.invoiceWhatsAppStatus === "failed" && val?.invoiceWhatsAppError === "not_on_whatsapp"
              ? "Not on WhatsApp"
              : val?.invoiceWhatsAppStatus === "failed" ? "Failed" : "Not sent",
        }
      })
      setReport(reports)
    } catch (error) {
      console.error(error)
    }
  }

  const columns = useMemo(
    () => [
      {
        Header: "Invoice No",
        accessor: "invoiceNo",
        filterable: true,
        Cell: cellProps => {
          return <SimpleStringValue {...cellProps} />
        },
      },
      {
        Header: "Invoice Date",
        accessor: "createdAt",
        filterable: true,
        Cell: cellProps => {
          return dayjs(cellProps.row.original["createdAt"]).format("DD/MM/YYYY")
        },
      },
      {
        Header: "Customer Name",
        accessor: "userDetails.name",
        filterable: true,
        Cell: cellProps => {
          return <SimpleStringValue {...cellProps} />
        },
      },
      {
        Header: "Firm Name",
        accessor: "userDetails.firmName",
        filterable: true,
        Cell: cellProps => {
          return <SimpleStringValue {...cellProps} />
        },
      },

      {
        Header: "GST NO.",
        accessor: "userDetails.GSTNo",
        filterable: true,
        Cell: cellProps => {
          return <SimpleStringValue {...cellProps} />
        },
      },
      {
        Header: "Plan Name",
        accessor: "name",
        filterable: true,
        Cell: cellProps => {
          return <SimpleStringValue {...cellProps} />
        },
      },
      {
        Header: "Renewal",
        accessor: "isPlanRenew",
        filterable: true,
        Cell: ({ row }) => {
          const value = row.original?.isPlanRenew;
          return value ? "Renew" : "New";
        },
      },
      {
        Header: "Payment Status",
        accessor: "paymentStatus",
        filterable: true,
        Cell: cellProps => {
          const data = cellProps.row.original
          return (
            <Badge color={data.paymentStatus == "paid" ? "success" : "warning"}>
              {data.paymentStatus}
            </Badge>
          )
        },
      },
      {
        Header: "Status",
        accessor: "invoiceStatus",
        filterable: true,
        Cell: cellProps => {
          const data = cellProps.row.original
          const isCancelled = data?.invoiceStatus === "cancelled" || data?.isInvoiceCancelled
          return (
            <Badge color={isCancelled ? "danger" : "success"}>
              {isCancelled ? "Cancelled" : "Paid"}
            </Badge>
          )
        },
      },
      {
        Header: "Plan Amount",
        accessor: "planAmount",
        filterable: true,
        Cell: cellProps => {
          return <SimpleStringValue {...cellProps} />
        },
      },
      {
        Header: "Discount Amount",
        accessor: "discountAmount",
        filterable: true,
        Cell: cellProps => {
          return <SimpleStringValue {...cellProps} />
        },
      },
      //* taxable amount = plan amount - discount amount
      {
        Header: "Taxable Amount",
        filterable: true,
        Cell: cellProps => {
          let data = cellProps.row.original
          if (data.discountAmount) {
            return <SimpleStringValue value={parseInt(data.planAmount - data.discountAmount)} />
          }
          return <SimpleStringValue value={data.planAmount} />
        }
      },
      {
        Header: "Tax Amount",
        accessor: "taxAmount",
        filterable: true,
        Cell: cellProps => {
          return <SimpleStringValue {...cellProps} />
        },
      },
      {
        Header: "Total Amount",
        accessor: "finalAmount",
        filterable: true,
        Cell: cellProps => {
          return <SimpleStringValue {...cellProps} />
        },
      },
      {
        Header: "Invoice",
        accessor: "invoiceUrl",
        filterable: false,
        Cell: cellProps => {
          const row = cellProps?.row?.original
          const invoiceUrl = row?.invoiceUrl
          if (invoiceUrl) {
            return (
              <a
                href={invoiceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-outline-primary"
                download
              >
                Download
              </a>
            )
          }

          const paymentId = row?._id
          const isPaid = row?.paymentStatus === "paid" && row?.invoiceStatus !== "cancelled" && !row?.isInvoiceCancelled
          if (paymentId && isPaid) {
            const isRegenerating = regeneratingId === paymentId
            return (
              <Button
                color="warning"
                outline
                size="sm"
                disabled={isRegenerating}
                onClick={() => handleRegenerateInvoice(paymentId)}
                title="Regenerate invoice PDF"
              >
                {isRegenerating ? (
                  <>
                    <i className="bx bx-loader bx-spin font-size-12 align-middle me-1"></i>
                    Generating...
                  </>
                ) : (
                  <>
                    <i className="bx bx-refresh font-size-12 align-middle me-1"></i>
                    Regenerate PDF
                  </>
                )}
              </Button>
            )
          }

          return "-"
        },
      },
      {
        Header: <div style={{ minWidth: "125px" }}>Invoice Send</div>,
        accessor: "invoiceWhatsAppStatus",
        filterable: false,
        Cell: cellProps => (
          <div style={{ minWidth: "125px" }}>
            <InvoiceWhatsAppCell
              payment={cellProps.row.original}
              onSent={() => fetchReport(date.startDate, date.endDate)}
            />
          </div>
        ),
      },
    ],
    [regeneratingId, date.startDate, date.endDate]
  )

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Report" />
        <Row>
          <Col xs="12">
            <Card>
              <CardBody>
                <div style={{ position: "absolute", left: "35%", zIndex: "1" }}>
                  <DateRangePicker
                    ranges={getPredefinedDateRanges()}
                    onClean={() => {
                      fetchReport()
                      setDate({
                        startDate:
                          dayjs(getUserInfo()?.createdAt).format("YYYY-MM-DD") +
                          "T00:00:00.000Z",
                        endDate:
                          dayjs().format("YYYY-MM-DD") + "T23:59:59.000Z",
                      })
                    }}
                    defaultValue={[
                      new Date(date.startDate),
                      new Date(date.endDate),
                    ]}
                    placeholder="selected Date"
                    className="range-picker mb-2 date-picker-div"
                    onChange={e => {
                      if (e !== null) {
                        let [startDate, endDate] = e
                        startDate =
                          dayjs(startDate).format("YYYY-MM-DD").split("T")[0] +
                          "T00:00:00.000Z"
                        endDate =
                          dayjs(endDate).format("YYYY-MM-DD").split("T")[0] +
                          "T00:00:00.000Z"
                        setDate({
                          startDate: startDate,
                          endDate: endDate,
                        })
                        fetchReport(startDate, endDate)
                      }
                    }}
                    showOneCalendar
                    format="dd/MM/yyyy"
                  />
                </div>
                <div
                  className="d-flex align-items-center gap-3"
                  style={{ position: "absolute", right: "28px", zIndex: "1" }}
                >
                  <Button color="primary" className="d-inline-flex align-items-center">
                    <CSVLink
                      filename={`Bill-Saathi-${dayjs(date.startDate).format(
                        "DD/MM/YYYY"
                      )} - ${dayjs(date.endDate).format("DD/MM/YYYY")}`}
                      data={csvReportData}
                      className="csv-export-button text-white"
                    >
                      <i className="mdi mdi-export-variant me-1" />
                      Export CSV
                    </CSVLink>
                  </Button>
                  <Button color="primary" onClick={exportXlsx} className="d-inline-flex align-items-center">
                    <i className="mdi mdi-file-excel me-1" />
                    Export Excel
                  </Button>
                </div>

                <TableContainer
                  columns={columns}
                  data={reportList}
                  isGlobalFilter={true}
                  isAddOptions={true}
                  canExportCsv={false}
                  customPageSize={100}
                  className="custom-header-css"
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default Index
